import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { combineLatest, forkJoin, ObservableInput, Subscription } from "rxjs";
import { ConfigService } from "../../../config/config.service";
import { Roles } from "../../../models/enums";
import { AppState } from "../../../store/app.state";
import {
  GetNewsAction,
  SetNewsSearchTextAction,
} from "../../../store/news/actions";
import { NewsService } from "src/app/services/news.service";
import { UserModel } from "src/app/models/user.model";
import { INews, NewsOperationType } from "src/app/models/news.model";
import { BootstrapAlertService } from "src/app/widgets/ng-bootstrap-alert/bootstrap-alert.service";
import { BootstrapAlert } from "src/app/widgets/ng-bootstrap-alert/bootstrap-alert";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { NewsEditModalComponent } from "./news-edit/news-edit-modal.component";
import { ConfirmComponent } from "../../common/confirm/confirm.component";
import { ConfirmWindowComponent } from "../../confirm-window/confirm-window.component";
import { AlertService } from "src/app/services/alert.service";
import { ScrollTrackerService } from "src/app/services/scrollTracker.service";
@Component({
  selector: "app-news",
  templateUrl: "./news.component.html",
  styleUrls: ["./news.component.scss"],
})
export class NewsComponent implements OnInit, OnDestroy {
  private newsSubscription: Subscription;
  private userAndSearchSubscription: Subscription;

  newsList: INews[] = [];
  isLeagueRole = false;
  isLoading = true;
  downloading = false;
  nextItemsUrl: string;
  prevItemsUrl: string;
  currentUrl: string;
  urgentFeature: boolean;
  currentUser: UserModel;
  searchText: string = "";
  resultCount: number;
  mainContainer: HTMLElement;
  modalEditInstance: NewsEditModalComponent;

  constructor(
    private router: Router,
    protected store: Store<AppState>,
    private configService: ConfigService,
    private readonly newsService: NewsService,
    private readonly bootstrapAlertService: BootstrapAlertService,
    private readonly alertService: AlertService,
    private readonly modalService: NgbModal,
    private scrollTrackerService: ScrollTrackerService,
  ) {
    this.nextItemsUrl = null;
    this.prevItemsUrl = null;
    this.currentUrl = "";
  }

  ngOnInit() {
    this.urgentFeature = this.configService.get(
      "features.news.urgentNotifications",
    );

    this.userAndSearchSubscription = combineLatest([
      this.store.select((state) => state.news.searchText),
      this.store.select((state) => state.user.currentUser),
    ]).subscribe(([searchText, user]) => {
      this.searchText = searchText || "";

      if (user?.role) {
        const hasUserChanged =
          !this.currentUser ||
          this.currentUser.id !== user.id ||
          this.currentUser.role !== user.role;

        this.currentUser = user;
        this.isLeagueRole = user.role === Roles.League;

        if (hasUserChanged) {
          this.store.dispatch(
            new GetNewsAction(this.setSearchUrl(), this.isLeagueRole),
          );
        }
      }
    });

    this.newsSubscription = this.store
      .select((state) => state.news)
      .subscribe((result) => {
        if (result.getLoading) {
          return;
        }
        this.newsList = result.allNews?.items;
        this.isLoading = result.getLoading;
        this.currentUrl = result.allNews?.currentUrl;
        this.resultCount = result.allNews?.totalCount;
        this.nextItemsUrl = result.allNews?.nextUrl;
        this.prevItemsUrl = result.allNews?.previousUrl;

        setTimeout(() => {
          this.setMainContainer();
          this.restoreScrollPosition();
        });
      });
  }

  setMainContainer() {
    this.mainContainer = document.getElementById("mainContainer");
  }

  restoreScrollPosition() {
    const { top } = this.scrollTrackerService.position;
    if (top > 0) {
      (this.mainContainer?.scrollTo({ top }), 0);
    }
  }

  readClick(item: INews) {
    this.router.navigate(
      [`/news/detail/${this.isLeagueRole ? item.entityId : item.id}`],
      {
        queryParams: { published: !this.isLeagueRole },
      },
    );
  }

  editClick(event: Event, item: INews) {
    event.stopPropagation();
    const modalRef = this.modalService.open(NewsEditModalComponent, {
      centered: true,
      backdrop: "static",
      keyboard: false,
      modalDialogClass: "news-edit-modal-dialog",
    });
    this.modalEditInstance =
      modalRef.componentInstance as NewsEditModalComponent;
    this.modalEditInstance.data = {
      newsItem: item,
      user: this.currentUser,
      type: NewsOperationType.Put,
    };
    this.modalEditInstance.finishNewsEdit.subscribe((res) => {
      if (res) {
        this.updateNewsList();
      }
    });
  }

  cloneClick(event: Event, item: INews) {
    event.stopPropagation();
    this.isLoading = true;
    const cloneProgress = this.newsService.clone(item.id, this.currentUser);

    const allProgressObservables: ObservableInput<any>[] = [];
    for (const key in cloneProgress) {
      if (cloneProgress.hasOwnProperty(key)) {
        allProgressObservables.push(cloneProgress[key].progress);
      }
    }

    forkJoin(allProgressObservables).subscribe({
      next: () => {
        this.isLoading = false;
        this.updateNewsList();
      },
      error: (error) => {
        this.isLoading = false;
        this.alertService.warning(
          `${error.message} ${error.error?.ExceptionMessage}`,
        );
      },
    });
  }

  archiveClick(item: INews) {
    const modalRef = this.modalService.open(ConfirmWindowComponent, {
      centered: true,
      backdrop: "static",
      keyboard: false,
    });
    const instance = modalRef.componentInstance as ConfirmWindowComponent;
    instance.item = item;
    const s = instance.confirmationResult.subscribe((res) => {
      if (res) {
        this.archiveAsset(item);
        s.unsubscribe();
      }
    });
  }

  archiveAsset(asset: INews) {
    this.isLoading = true;
    const f = this.newsService
      .archiveNews(asset.id)

      .subscribe(
        () => {
          this.updateNewsList();
          f.unsubscribe();
        },
        (error) => {
          const message = `${error.message} ${error.error?.ExceptionMessage}`;
          this.alertService.warning(message);
          this.updateNewsList();
          f.unsubscribe();
        },
      );
  }

  fetchPrev() {
    this.store.dispatch(new GetNewsAction(this.prevItemsUrl));
  }

  fetchNext() {
    this.store.dispatch(new GetNewsAction(this.nextItemsUrl));
  }

  getPagesInfo() {
    if (this.nextItemsUrl === null && this.prevItemsUrl === null) {
      return `1 to ${this.resultCount}`;
    }

    const pagesInfoUrl = this.nextItemsUrl
      ? this.nextItemsUrl
      : this.prevItemsUrl;
    const url = new URL(pagesInfoUrl, globalThis.location.origin);
    const skipFromUrl = Number(url.searchParams.get("skip"));
    const limitFromUrl = Number(url.searchParams.get("limit"));
    let first = skipFromUrl + limitFromUrl;
    let last = this.resultCount;

    if (this.nextItemsUrl) {
      first = skipFromUrl - limitFromUrl + 1;
      last = skipFromUrl;
    }
    return `${first} - ${last} of ${this.resultCount}`;
  }

  setSearchQueryParams(): string {
    if (!this.searchText) {
      return "";
    }
    const queryString = `search=${encodeURIComponent(this.searchText)}`;
    return queryString ? "?" + queryString : "";
  }

  setSearchUrl(): string {
    const queryString = this.setSearchQueryParams();
    const searchUrl = `${this.currentUrl.split("?")[0]}${queryString}`;
    return searchUrl;
  }

  submitSearch() {
    setTimeout(() => {
      this.store.dispatch(new SetNewsSearchTextAction(this.searchText));
      this.store.dispatch(new GetNewsAction(this.setSearchUrl()));
    });
  }

  updateNewsList() {
    this.store.dispatch(new GetNewsAction(this.currentUrl));
  }

  confirmPublish(event: Event, item: INews) {
    event.stopPropagation();
    const modalRef = this.modalService.open(ConfirmComponent, {
      centered: true,
    });
    const instance = modalRef.componentInstance as ConfirmComponent;
    instance.message = item.published
      ? "By unpublishing the news, all users will lose access to the news item; are you sure you want to continue?"
      : "By publishing the news, all users identified using the Application, Role, and Competition criteria will receive a notification; are you sure you want to continue?";
    instance.confirmText = item.published ? "Unpublish" : "Publish";
    instance.cancelText = "Cancel";

    const s = instance.confirm.subscribe((_) => {
      this.togglePublish(event, item);
      s.unsubscribe();
    });
  }

  togglePublish(event: Event, asset: INews) {
    event.stopPropagation();
    const togglePublishData = {
      published: !asset?.published,
      lastUpdatedBy: this.currentUser?.firstName,
      lastUpdatedById: this.currentUser?.id,
    };
    const f = this.newsService
      .setPublishedStatus(asset.id, togglePublishData)
      .subscribe(
        (res) => {
          this.bootstrapAlertService.alert(
            new BootstrapAlert(
              "News publish status updated successfully",
              "alert-success",
            ),
          );
          this.updateNewsList();
          f.unsubscribe();
        },
        (error) => {
          this.bootstrapAlertService.alert(
            new BootstrapAlert(
              `${error.message} ${error.error?.ExceptionMessage}`,
              "alert-danger",
            ),
          );
          f.unsubscribe();
        },
      );
  }

  searchKeypressHandler(event: KeyboardEvent) {
    if (event.code === "Enter" || event.keyCode === 13) {
      this.scrollTrackerService.reset();
      this.submitSearch();
    }
  }

  clearAllTags() {
    this.resetSearch();
    this.submitSearch();
  }

  resetSearch() {
    this.searchText = "";
    this.store.dispatch(new SetNewsSearchTextAction(""));
    this.scrollTrackerService.reset();
  }

  ngOnDestroy(): void {
    if (this.userAndSearchSubscription) {
      this.userAndSearchSubscription.unsubscribe();
    }
    if (this.newsSubscription) {
      this.newsSubscription.unsubscribe();
    }

    this.scrollTrackerService.storePosition(
      this.mainContainer.scrollTop,
      this.mainContainer.scrollLeft,
    );

    if (this.modalEditInstance) {
      this.modalEditInstance.finishNewsEdit.unsubscribe();
    }
  }
}
